import { useState, useEffect } from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { paymentService } from "../../services/paymentService";

interface Payment {
  _id: string;
  transactionId: string;
  status: string;
  amount: number;
  currency: string;
}

interface PaymentStatusDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  payment: Payment | null;
}

export function PaymentStatusDialog({
  isOpen,
  onClose,
  onSuccess,
  payment,
}: PaymentStatusDialogProps) {
  const [status, setStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (payment) {
      setStatus(payment.status);
      setError("");
    }
  }, [payment, isOpen]);

  if (!isOpen || !payment) return null;

  const handleSubmit = async () => {
    if (!status) {
      setError("Please select a status");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      await paymentService.updatePaymentStatus(payment._id, status);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to update payment status");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Update Payment Status"
      className="max-w-md"
    >
      <div className="space-y-4">
        <div className="p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-500">Transaction ID</p>
          <p className="font-mono text-sm font-medium text-gray-900">
            {payment.transactionId}
          </p>
          <p className="font-semibold text-gray-900">
            {payment.amount} {payment.currency}
          </p>
          {payment.paytabsTranRef && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">
                PayTabs Reference
              </p>
              <p className="font-mono text-xs text-gray-600">
                {payment.paytabsTranRef}
              </p>
            </div>
          )}
          
          
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Status
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
            {error}
          </p>
        )}

        <div className="flex gap-3 pt-6 border-t border-gray-100 mt-6">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="flex-1"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? "Updating..." : "Update Status"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
